import { useEffect, useState } from 'react'
import { Loader2, RefreshCw, Trophy } from 'lucide-react'
import { algorithmicScannerService } from '@/services/algorithmicScanner'

function pct(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return `${(Number(v) * 100).toFixed(1)}%`
}

function num(v, digits = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return Number(v).toFixed(digits)
}

const INDICATOR_LABELS = {
  ict: 'ICT / SMC',
  showi_ict: 'SHOWI ICT',
  squeeze_breakout: 'Squeeze Breakout',
  smart_breakout: 'Smart Breakout',
  breakout_pattern: 'Patrón de Ruptura',
  canales_sr: 'Canales S/R',
  liquidity_sweep_pro: 'Liquidity Sweep Pro',
  supertrend_quantum: 'SuperTrend Quantum',
}

function labelFor(id) {
  return INDICATOR_LABELS[id] || id
}

function toneFor(row) {
  if (row.total_r > 0 && row.win_rate && row.win_rate >= 0.5) return 'good'
  if (row.total_r < 0 || (row.win_rate !== null && row.win_rate < 0.4)) return 'bad'
  return 'neutral'
}

export default function SignalIndicatorRanking({ scannerId = null }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    load()
  }, [scannerId])

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data: res } = scannerId
        ? await algorithmicScannerService.getScannerIndicatorRankings(scannerId)
        : await algorithmicScannerService.getGlobalIndicatorRankings()
      setData(res)
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cargar el ranking de indicadores')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="animate-spin text-slate-400" size={24} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-red-500/10 text-red-700 dark:text-red-300 text-sm border border-red-500/30">
        <span>{error}</span>
        <button onClick={load} className="text-xs underline">Reintentar</button>
      </div>
    )
  }

  const items = data?.items || []

  if (items.length === 0) {
    return (
      <div className="text-center py-6 text-slate-500 dark:text-gray-400 text-sm">
        <p>Aún no hay señales registradas por indicador.</p>
        <p className="text-xs mt-1">Ejecuta el escáner para que aparezcan los indicadores activos.</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="section-title flex items-center gap-2">
          <Trophy size={16} />
          Ranking de indicadores por señales
        </h3>
        <button
          onClick={load}
          disabled={loading}
          className="text-[10px] text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200 flex items-center gap-1"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refrescar
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-gray-400">
            <tr>
              <th className="text-left p-2 rounded-l-lg">#</th>
              <th className="text-left p-2">Indicador</th>
              <th className="text-right p-2">Señales</th>
              <th className="text-right p-2">Resueltas</th>
              <th className="text-right p-2">Pendientes</th>
              <th className="text-right p-2">Win rate</th>
              <th className="text-right p-2">Profit factor</th>
              <th className="text-right p-2">Sharpe</th>
              <th className="text-right p-2">Total R</th>
              <th className="text-right p-2">Avg R</th>
              <th className="text-right p-2 rounded-r-lg">Máx. pérdidas</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row, idx) => {
              const tone = toneFor(row)
              return (
                <tr
                  key={row.indicator_id}
                  className="border-b border-slate-200 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-800/50"
                >
                  <td className="p-2 font-bold text-slate-700 dark:text-gray-200">{idx + 1}</td>
                  <td className="p-2">
                    <span className="font-medium text-slate-900 dark:text-white">{labelFor(row.indicator_id)}</span>
                    <span className="block text-[10px] text-slate-500 dark:text-gray-400 font-mono">{row.indicator_id}</span>
                  </td>
                  <td className="text-right p-2">{row.total_signals}</td>
                  <td className="text-right p-2">{row.resolved_signals}</td>
                  <td className="text-right p-2">{row.pending_signals}</td>
                  <td className={`text-right p-2 ${tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' : tone === 'bad' ? 'text-rose-600 dark:text-rose-400' : ''}`}>
                    {pct(row.win_rate)}
                  </td>
                  <td className="text-right p-2">{num(row.profit_factor)}</td>
                  <td className="text-right p-2">{num(row.sharpe_ratio)}</td>
                  <td className={`text-right p-2 font-semibold ${row.total_r >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                    {row.total_r >= 0 ? '+' : ''}{num(row.total_r)}
                  </td>
                  <td className="text-right p-2">{num(row.avg_r)}</td>
                  <td className="text-right p-2">{row.max_consecutive_losses}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
